<template>
  <div>
    <div
      v-if="user.role === 'manager'"
      class="d-flex align-items-center mt-3"
    >
      <label class="mr-1 h3">Gestion des bons d'échange</label>
    </div>
    <!-- input Recherche -->
    <div class="custom-search d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-center">
        <b-button
          v-if="selectedVouchers.length > 0"
          variant="primary"
          class="mr-2"
          @click="bulkPrintVouchers"
        >
          <feather-icon icon="PrinterIcon" />
          <span class="ml-1">Imprimer {{ selectedVouchers.length }} bon(s)</span>
        </b-button>
        <b-button
          v-if="selectedVouchers.length > 0"
          variant="outline-secondary"
          @click="clearSelection"
        >
          <feather-icon icon="XIcon" />
          <span class="ml-1">Annuler la sélection</span>
        </b-button>
      </div>
      <div class="d-flex">
        <b-form-group class="mb-0 mr-2">
          <div class="d-flex align-items-center">
            <label class="mr-1 mb-0">Recherche de bon d'échange</label>
            <b-form-input
              v-model="searchTerm"
              placeholder="Rechercher par code..."
              type="text"
              class="d-inline-block"
            />
          </div>
        </b-form-group>
        <b-form-group class="mb-0">
          <b-button
            v-if="user.role !== 'supplier'"
            :to="{ name: 'bon-echange-add' }"
            variant="gradient-primary"
            class="d-inline-block"
          >
            <feather-icon icon="PlusIcon" />
            <span class="ml-1">Créer un bon d'échange</span>
          </b-button>
        </b-form-group>
      </div>
    </div>
    <div
      v-if="isLoading === true"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Text Centered"
      />
    </div>
    <!-- table -->
    <vue-good-table
      class="mb-3"
      mode="remote"
      :current-page="currentPage"
      :columns="columns"
      :rows="exchangeVouchers"
      :total-rows="count"
      :rtl="direction"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm }"
      :select-options="{
        enabled: true,
        selectOnCheckboxOnly: true,
        selectionInfoClass: 'custom-class',
        selectionText: 'lignes sélectionnées',
        clearSelectionText: 'Effacer',
      }"
      :pagination-options="{
        enabled: true,
        perPage:pageLength
      }"
      @on-column-filter="onColumnFilter"
      @on-selected-rows-change="onSelectionChanged"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <div
          v-if="props.column.field === 'id'"
          class="text-nowrap"
        >
          <qrcode-vue
            style="display:inline"
            :value="props.row.id.toString()"
            :size="size"
            level="H"
          />
          <span class="ml-2">{{ props.row.id }}</span>

        </div>
        <!-- Column: Payment -->
        <span v-else-if="props.column.field === 'payment'">
          <b-badge
            v-if="props.row.payment"
            variant="light-info"
            class="cursor-pointer"
            @click="$router.push({ name: 'payment-show', params: { id: props.row.payment } })"
          >
            <feather-icon
              icon="FileTextIcon"
              size="14"
              class="mr-1"
            />
            Paiement #{{ props.row.payment }}
          </b-badge>
          <span v-else>-</span>
        </span>

        <!-- Column: Orders Count -->
        <span v-else-if="props.column.field === 'orders_count'">
          <b-badge variant="light-primary">
            {{ props.row.orders_count }} colis
          </b-badge>
        </span>

        <!-- Column: Total Value -->
        <span v-else-if="props.column.field === 'total_value'">
          <strong>{{ formatAmount(props.row.total_value) }} TND</strong>
        </span>

        <!-- Column: Printed Status -->
        <span v-else-if="props.column.field === 'is_printed'">
          <b-badge :variant="props.row.is_printed ? 'light-success' : 'light-warning'">
            <feather-icon
              :icon="props.row.is_printed ? 'CheckCircleIcon' : 'ClockIcon'"
              size="14"
              class="mr-1"
            />
            {{ props.row.is_printed ? 'Imprimé' : 'Non imprimé' }}
          </b-badge>
        </span>

        <!-- Column: Action -->
        <span v-else-if="props.column.field === 'action'">
          <span>
            <feather-icon
              :id="`invoice-row-${props.row.id}-show-icon`"
              icon="EyeIcon"
              class="cursor-pointer mr-1"
              size="16"
              @click="
                $router.push({
                  name: 'bon-echange-show',
                  params: { id: props.row.id} ,
                })
              "
            />
            <b-tooltip
              title="Détail"
              class="cursor-pointer"
              :target="`invoice-row-${props.row.id}-show-icon`"
            />
            <feather-icon
              :id="`invoice-row-${props.row.id}-print-icon`"
              icon="PrinterIcon"
              class="cursor-pointer mr-1"
              size="16"
              @click="printSingleVoucher(props.row.id)"
            />
            <b-tooltip
              title="Imprimer"
              class="cursor-pointer"
              :target="`invoice-row-${props.row.id}-print-icon`"
            />
            <b-button
              v-if="isSupplier && props.row.generated_order_id && props.row.generated_order_status === 25"
              variant="success"
              size="sm"
              class="ml-1"
              @click="markAsReceived(props.row)"
            >
              Reçu
            </b-button>
            <b-badge
              v-else-if="isSupplier && props.row.generated_order_status === 26"
              variant="light-success"
              class="ml-1"
            >
              Reçu ✓
            </b-badge>
          </span>
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap">
              Affichage 1 à            </span>
            <b-form-select
              v-model="pageLength"
              :options="['50','100','500']"
              class="mx-1"
              @input="value => onPageLengthChange(value, props)"
            />
            <span class="text-nowrap "> de {{ count }} entrées
            </span>
          </div>

          <div>
            <b-pagination
              v-model="currentPage"
              :total-rows="count"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="pageNext(currentPage)"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </div>
</template>

<script>
import {
  BBadge, BFormGroup, BFormInput, BButton,
  BSpinner, BTooltip, BPagination, BFormSelect,

} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import store from '@/store/index'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import storeAuth from '@/store/store'
import QrcodeVue from 'qrcode.vue'

export default {
  components: {
    VueGoodTable,
    BBadge,
    BFormGroup,
    BFormInput,
    BButton,
    BSpinner,
    BTooltip,
    QrcodeVue,
    BPagination,
    BFormSelect,

  },
  data() {
    return {
      currentPage: 1,
      count: 0,
      size: 80,
      user: {},
      status: 0,
      isLoading: false,
      pageLength: 100,
      dir: false,
      selectedVouchers: [],
      columns: [
        {
          label: 'Code',
          field: 'id',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher code',
          },
        },
        {
          label: 'Date et heure',
          field: 'created_at',
          formatFn: this.formatFn,
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher date',
          },
        },
        {
          label: 'Paiement',
          field: 'payment',
        },
        {
          label: 'Nombre de colis',
          field: 'orders_count',
        },
        {
          label: 'Statut impression',
          field: 'is_printed',
        },
        {
          label: 'Action',
          field: 'action',
        },
      ],
      exchangeVouchers: [],
      searchTerm: '',
      columnFilters: {},
      searchDebounceTimer: null,
    }
  },
  computed: {
    statusVariant() {
      const statusColor = {
        /* eslint-disable key-spacing */
        Current      : 'light-primary',
        Professional : 'light-success',
        Rejected     : 'light-danger',
        Resigned     : 'light-warning',
        Applied      : 'light-info',
        /* eslint-enable key-spacing */
      }

      return status => statusColor[status]
    },
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
    userRole() {
      return storeAuth.getters.userRole || this.user.role
    },
    isSupplier() {
      return this.userRole === 'supplier'
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.getExchangeVouchers(1)
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer)
    }
  },
  watch: {
    searchTerm() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.currentPage = 1
        this.getExchangeVouchers(1)
      }, 300)
    },
  },
  methods: {
    async pageNext(currentPage) {
      this.getExchangeVouchers(currentPage)
    },
    async onPageLengthChange(value, props) {
      const perPage = Number(value)
      props.perPageChanged({ currentPerPage: perPage })
      this.pageLength = perPage
      this.currentPage = 1
      await this.getExchangeVouchers(1)
    },
    async onColumnFilter(params) {
      this.columnFilters = params.columnFilters || {}
      this.currentPage = 1
      await this.getExchangeVouchers(1)
    },
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
    formatAmount(value) {
      return Number(value || 0).toFixed(3)
    },
    async getExchangeVouchers(currentPage) {
      if (this.status === 0) {
        this.isLoading = true
        const codeFilter = String(this.columnFilters.id || this.searchTerm || '').trim()
        const params = {
          page: currentPage,
          page_size: this.pageLength,
        }
        if (codeFilter) {
          params.code = codeFilter
        }
        const { data } = await axios.get('api/exchange-vouchers/list/', {
          params,
        })
        this.count = data.count
        this.exchangeVouchers = data.results
        this.isLoading = false
      } else {
        this.isLoading = true
        const { data } = await axios.get('api/exchange-vouchers/')
        this.exchangeVouchers = data
        this.isLoading = false
      }
    },
    onSelectionChanged(params) {
      this.selectedVouchers = params.selectedRows
    },
    clearSelection() {
      this.selectedVouchers = []
    },
    async printSingleVoucher(voucherId) {
      this.$router.push({
        name: 'bon-echange-show',
        params: { id: voucherId },
      })
    },
    async bulkPrintVouchers() {
      if (this.selectedVouchers.length === 0) {
        this.showToast('warning', 'top-center', 'Veuillez sélectionner au moins un bon d\'échange')
        return
      }

      try {
        const voucherIds = this.selectedVouchers.map(v => v.id)

        // Call bulk print API
        const { data } = await axios.post('/api/exchange-vouchers/bulk-print/', {
          exchange_voucher_ids: voucherIds,
          mark_as_printed: true,
        })

        this.showToast('success', 'top-center', `${data.total_vouchers} bon(s) d'échange préparé(s) pour l'impression`)

        // Open print window with all vouchers
        this.openBulkPrintWindow(data.print_data)

        // Refresh the list to show updated print status
        await this.getExchangeVouchers(this.currentPage)

        // Clear selection
        this.clearSelection()
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors de la préparation de l\'impression groupée')
        // eslint-disable-next-line no-console
        console.error('Bulk print error:', error)
      }
    },
    async markAsReceived(voucher) {
      if (!this.isSupplier) {
        return
      }
      try {
        await axios.post(`/api/exchange-vouchers/mark-voucher-received/${voucher.id}/`)
        this.exchangeVouchers = this.exchangeVouchers.map(row => (
          row.id === voucher.id
            ? { ...row, generated_order_status: 26 }
            : row
        ))
        this.showToast('success', 'top-center', `Bon d'échange #${voucher.id} marqué comme Échange reçu`)
      } catch (error) {
        const msg = error.response?.data?.error || 'Erreur lors de la mise à jour du statut'
        this.showToast('danger', 'top-center', msg)
      }
    },
    openBulkPrintWindow(vouchersData) {
      // Create a new window for bulk printing
      const printWindow = window.open('', '_blank', 'width=800,height=600')

      if (!printWindow) {
        this.showToast('danger', 'top-center', 'Impossible d\'ouvrir la fenêtre d\'impression. Vérifiez les paramètres de votre navigateur.')
        return
      }

      // Generate HTML for all vouchers
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Impression groupée - Bons d'échange</title>
          <style>
            @media print {
              @page { margin: 1cm; }
              .page-break { page-break-after: always; }
            }
            body { font-family: Arial, sans-serif; }
            .voucher-container { margin-bottom: 2cm; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid black; padding: 10px; text-align: left; }
            th { background-color: #f0f0f0; }
            .header { text-align: center; margin-bottom: 20px; }
            .info { margin: 10px 0; }
          </style>
        </head>
        <body>
      `

      vouchersData.forEach((voucher, index) => {
        const isLastVoucher = index === vouchersData.length - 1
        htmlContent += `
          <div class="voucher-container ${!isLastVoucher ? 'page-break' : ''}">
            <div class="header">
              <h1>Bon d'échange #${voucher.id}</h1>
              <p>Date: ${this.formatFn(voucher.created_at)}</p>
            </div>
            <div class="info">
              <p><strong>Nombre de colis:</strong> ${voucher.orders_count}</p>
              <p><strong>Valeur totale:</strong> ${(voucher.total_value || 0).toFixed(3)} TND</p>
              ${voucher.payment ? `<p><strong>Paiement:</strong> #${voucher.payment}</p>` : ''}
            </div>
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Destinataire</th>
                  <th>Téléphone</th>
                  <th>Adresse</th>
                  <th>Prix TTC</th>
                </tr>
              </thead>
              <tbody>
        `

        voucher.orders.forEach(order => {
          htmlContent += `
                <tr>
                  <td>${order.id}</td>
                  <td>${order.name}</td>
                  <td>${order.phone1}</td>
                  <td>${order.address}</td>
                  <td>${order.price_ttc} TND</td>
                </tr>
          `
        })

        htmlContent += `
              </tbody>
            </table>
            <div style="margin-top: 50px;">
              <p><strong>Signature:</strong> _______________________</p>
            </div>
          </div>
        `
      })

      htmlContent += `
        </body>
        </html>
      `

      printWindow.document.write(htmlContent)
      printWindow.document.close()

      // Wait for content to load then print
      printWindow.onload = () => {
        printWindow.print()
      }
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
  },
}
</script>
<style lang="scss" >
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
